import {
  type CreateUploadSessionRequest,
  type DocumentSource,
  type DuplicateDocument,
  duplicateDocumentSchema,
  mimeTypeFromFilename,
  validateUploadCandidate,
} from '@hvac/contracts';

/**
 * The upload queue.
 *
 * A hundred receipts dragged into a browser is the workload this is built for.
 * That volume makes three things non-negotiable:
 *
 *  - **Bounded concurrency.** Hashing holds a whole file in memory and uploads
 *    compete for one connection pool. Unbounded parallelism means a stalled
 *    browser tab and a router that gives up.
 *  - **Independent failure.** File 63 failing must not disturb the other 99,
 *    and must be retryable on its own.
 *  - **Honest progress.** Hashing is a real fraction of the wait on a large
 *    scan, so it is part of the progress a user sees, not a silent pause.
 *
 * Deliberately framework-free: no React, no fetch of its own. The transport
 * and the storage are injected, which is what makes the state machine testable
 * without a browser, a network, or a running Supabase.
 */

export type UploadItemStatus =
  | 'queued'
  | 'hashing'
  | 'requesting'
  | 'uploading'
  | 'finalising'
  | 'completed'
  | 'duplicate'
  | 'failed'
  | 'cancelled';

export interface UploadItem {
  readonly id: string;
  readonly file: File;
  readonly filename: string;
  readonly byteSize: number;
  readonly mimeType: string;
  readonly status: UploadItemStatus;
  /** 0..1 across the whole item, hashing included. */
  readonly progress: number;
  readonly sha256: string | null;
  readonly sessionId: string | null;
  readonly documentId: string | null;
  readonly error: string | null;
  readonly duplicate: DuplicateDocument | null;
  readonly attempt: number;
  readonly startedAt: number | null;
  readonly finishedAt: number | null;
}

/** Everything the manager needs from the outside world. */
export interface UploadTransport {
  createSession(request: CreateUploadSessionRequest): Promise<{
    sessionId: string;
    bucket: string;
    objectKey: string;
    uploadUrl: string;
    token: string;
  }>;
  /** Resolves when the bytes are stored. Must honour the abort signal. */
  putBytes(params: {
    uploadUrl: string;
    token: string;
    file: File;
    mimeType: string;
    signal: AbortSignal;
    onProgress: (fraction: number) => void;
  }): Promise<void>;
  uploadPreview(params: {
    organizationId: string;
    blob: Blob;
  }): Promise<{ objectKey: string; byteSize: number } | null>;
  completeSession(
    sessionId: string,
    body: {
      bytesUploaded: number;
      uploadStartedAt: string;
      uploadFinishedAt: string;
      pageCount?: number;
      preview?: { objectKey: string; width: number; height: number; byteSize: number };
    },
  ): Promise<{ documentId: string }>;
  abandonSession(sessionId: string, reason: string): Promise<void>;
}

export interface UploadManagerOptions {
  readonly organizationId: string;
  readonly source: DocumentSource;
  readonly transport: UploadTransport;
  readonly hashFile: (
    file: Blob,
    options: { signal?: AbortSignal; onProgress?: (fraction: number) => void },
  ) => Promise<{ sha256: string }>;
  readonly sniffMimeType: (file: Blob) => Promise<string | null>;
  readonly generatePreview: (
    file: File,
    mimeType: string,
  ) => Promise<{ blob: Blob; width: number; height: number } | null>;
  readonly readPageCount: (file: File, mimeType: string) => Promise<number | null>;
  /**
   * Three at a time. Chosen because it saturates a typical connection without
   * holding three large files in memory beyond what hashing already needs, and
   * because browsers cap concurrent connections per host anyway.
   */
  readonly concurrency?: number;
  readonly maxAttempts?: number;
  readonly onChange: (items: readonly UploadItem[]) => void;
  readonly onCompleted?: (item: UploadItem) => void;
}

/** Share of an item's progress bar given to hashing. */
const HASH_PROGRESS_SHARE = 0.15;

export interface UploadManager {
  add(files: readonly File[]): UploadItem[];
  retry(itemId: string): void;
  /** Re-runs a duplicate-blocked item, recording why the user wants it anyway. */
  overrideDuplicate(itemId: string, reason: string): void;
  cancel(itemId: string): void;
  cancelAll(): void;
  clearFinished(): void;
  items(): readonly UploadItem[];
}

export function createUploadManager(options: UploadManagerOptions): UploadManager {
  const concurrency = options.concurrency ?? 3;
  const maxAttempts = options.maxAttempts ?? 3;

  const items = new Map<string, UploadItem>();
  const controllers = new Map<string, AbortController>();
  const overrideReasons = new Map<string, string>();
  let active = 0;

  const snapshot = (): UploadItem[] => [...items.values()];
  const emit = (): void => options.onChange(snapshot());

  function patch(id: string, changes: Partial<UploadItem>): void {
    const current = items.get(id);
    if (current === undefined) return;
    items.set(id, { ...current, ...changes });
    emit();
  }

  function pump(): void {
    while (active < concurrency) {
      const next = snapshot().find((item) => item.status === 'queued');
      if (next === undefined) return;
      active += 1;
      // Marked immediately so the next loop iteration does not pick it again.
      patch(next.id, { status: 'hashing', startedAt: Date.now() });
      void run(next.id).finally(() => {
        active -= 1;
        pump();
      });
    }
  }

  async function run(id: string): Promise<void> {
    const item = items.get(id);
    if (item === undefined) return;

    const controller = new AbortController();
    controllers.set(id, controller);

    try {
      const { sha256 } = await options.hashFile(item.file, {
        signal: controller.signal,
        onProgress: (fraction) => patch(id, { progress: fraction * HASH_PROGRESS_SHARE }),
      });
      patch(id, { sha256, status: 'requesting', progress: HASH_PROGRESS_SHARE });

      const overrideReason = overrideReasons.get(id);
      const session = await options.transport.createSession({
        filename: item.filename,
        byteSize: item.byteSize,
        mimeType: item.mimeType,
        sha256,
        source: options.source,
        ...(overrideReason === undefined ? {} : { duplicateOverrideReason: overrideReason }),
      });
      patch(id, { sessionId: session.sessionId, status: 'uploading' });

      const uploadStartedAt = new Date().toISOString();
      await options.transport.putBytes({
        uploadUrl: session.uploadUrl,
        token: session.token,
        file: item.file,
        mimeType: item.mimeType,
        signal: controller.signal,
        onProgress: (fraction) =>
          patch(id, {
            progress: HASH_PROGRESS_SHARE + fraction * (1 - HASH_PROGRESS_SHARE),
          }),
      });
      const uploadFinishedAt = new Date().toISOString();
      patch(id, { status: 'finalising', progress: 1 });

      // A thumbnail is a nicety. Its failure must never fail the upload, so
      // both of these swallow their own errors and return null.
      const preview = await options.generatePreview(item.file, item.mimeType);
      const uploadedPreview =
        preview === null
          ? null
          : await options.transport
              .uploadPreview({ organizationId: options.organizationId, blob: preview.blob })
              .catch(() => null);
      const pageCount = await options.readPageCount(item.file, item.mimeType);

      const { documentId } = await options.transport.completeSession(session.sessionId, {
        bytesUploaded: item.byteSize,
        uploadStartedAt,
        uploadFinishedAt,
        ...(pageCount === null ? {} : { pageCount }),
        ...(preview !== null && uploadedPreview !== null
          ? {
              preview: {
                objectKey: uploadedPreview.objectKey,
                width: preview.width,
                height: preview.height,
                byteSize: uploadedPreview.byteSize,
              },
            }
          : {}),
      });

      patch(id, {
        status: 'completed',
        documentId,
        progress: 1,
        error: null,
        finishedAt: Date.now(),
      });
      const finished = items.get(id);
      if (finished !== undefined) options.onCompleted?.(finished);
    } catch (caught) {
      handleFailure(id, caught);
    } finally {
      controllers.delete(id);
    }
  }

  function handleFailure(id: string, caught: unknown): void {
    const item = items.get(id);
    if (item === undefined) return;

    if (caught instanceof DOMException && caught.name === 'AbortError') {
      patch(id, { status: 'cancelled', error: 'Cancelled.', finishedAt: Date.now() });
      return;
    }

    // A duplicate is not a failure; it is a question for the user. The details
    // arrived with the 409, so no second round trip is needed to ask it.
    const duplicate = extractDuplicate(caught);
    if (duplicate !== null) {
      patch(id, {
        status: 'duplicate',
        duplicate,
        error: 'This file has already been uploaded.',
        finishedAt: Date.now(),
      });
      return;
    }

    const message = caught instanceof Error ? caught.message : 'The upload failed.';
    const attempt = item.attempt + 1;

    // Retry transport failures automatically; a rejection the server explained
    // will not succeed on a second attempt and retrying only delays the error
    // the user needs to read.
    if (attempt < maxAttempts && isRetryable(caught)) {
      patch(id, { status: 'queued', attempt, error: message, progress: 0 });
      return;
    }
    patch(id, { status: 'failed', attempt, error: message, finishedAt: Date.now() });
  }

  return {
    add(files): UploadItem[] {
      const added: UploadItem[] = [];

      for (const file of files) {
        const id = crypto.randomUUID();
        // Browsers report MIME inconsistently, so the extension is the
        // fallback. Magic-byte sniffing happens during `run`, once the file is
        // being read anyway.
        const mimeType =
          file.type !== ''
            ? file.type
            : (mimeTypeFromFilename(file.name) ?? 'application/octet-stream');

        const rejection = validateUploadCandidate({
          filename: file.name,
          byteSize: file.size,
          mimeType,
        });

        const item: UploadItem = {
          id,
          file,
          filename: file.name,
          byteSize: file.size,
          mimeType,
          status: rejection === null ? 'queued' : 'failed',
          progress: 0,
          sha256: null,
          sessionId: null,
          documentId: null,
          error: rejection?.message ?? null,
          duplicate: null,
          attempt: 0,
          startedAt: null,
          finishedAt: rejection === null ? null : Date.now(),
        };
        items.set(id, item);
        added.push(item);
      }

      emit();
      pump();
      return added;
    },

    retry(itemId): void {
      const item = items.get(itemId);
      if (item === undefined || !['failed', 'cancelled'].includes(item.status)) return;
      patch(itemId, { status: 'queued', attempt: 0, error: null, progress: 0, finishedAt: null });
      pump();
    },

    overrideDuplicate(itemId, reason): void {
      const item = items.get(itemId);
      if (item?.status !== 'duplicate') return;
      overrideReasons.set(itemId, reason);
      patch(itemId, {
        status: 'queued',
        attempt: 0,
        error: null,
        duplicate: null,
        progress: 0,
        finishedAt: null,
      });
      pump();
    },

    cancel(itemId): void {
      controllers.get(itemId)?.abort();
      const item = items.get(itemId);
      if (item === undefined) return;
      if (item.status === 'queued') {
        patch(itemId, { status: 'cancelled', error: 'Cancelled.', finishedAt: Date.now() });
      }
      // Tell the server too, so the session is not left waiting for bytes that
      // will never arrive. Best-effort: the sweep collects it either way.
      if (item.sessionId !== null) {
        void options.transport
          .abandonSession(item.sessionId, 'Cancelled by the user.')
          .catch(() => {
            /* the expiry sweep will collect it */
          });
      }
    },

    cancelAll(): void {
      for (const item of snapshot()) {
        if (['queued', 'hashing', 'requesting', 'uploading', 'finalising'].includes(item.status)) {
          this.cancel(item.id);
        }
      }
    },

    clearFinished(): void {
      for (const item of snapshot()) {
        if (['completed', 'cancelled'].includes(item.status)) items.delete(item.id);
      }
      emit();
    },

    items: snapshot,
  };
}

/**
 * Pulls the existing document out of a 409.
 *
 * Parsed with the shared schema rather than trusted, because this drives a
 * dialog that tells the user something factual about their own data.
 */
function extractDuplicate(caught: unknown): DuplicateDocument | null {
  if (typeof caught !== 'object' || caught === null) return null;
  const withDetails = caught as { details?: { duplicate?: unknown } };
  const parsed = duplicateDocumentSchema.safeParse(withDetails.details?.duplicate);
  return parsed.success ? parsed.data : null;
}

/** Network and 5xx failures are worth another go; a 4xx is not. */
function isRetryable(caught: unknown): boolean {
  if (typeof caught !== 'object' || caught === null) return true;
  const status = (caught as { httpStatus?: unknown }).httpStatus;
  if (typeof status !== 'number') return true;
  return status >= 500 || status === 429;
}
